<template>
  <div class="add-apps">
    <div class="add-apps__content">
      <div class="add-apps__filters">
        <search-app v-model="snippetSearch" @change="clearFilters('category')" />
        <categories
          v-model="categoryTagsSelected"
          @change="clearFilters('snippet')"
          :items="isViewType(ViewTabOption.APPS) ? appsInCategories : serviceInCategories"
        />
      </div>

      <div class="add-apps__gallery">
        <div class="add-apps__view-controller">
          <v-btn
            @click="() => selectTab(ViewTabOption.APPS)"
            :class="['add-apps__button', isViewType(ViewTabOption.APPS) && 'add-apps__button--active']"
          >
            {{ $t("WorkspaceEdit.addApps.buttons.apps") }}
          </v-btn>

          <v-btn
            @click="() => selectTab(ViewTabOption.SERVICES)"
            :class="['add-apps__button', isViewType(ViewTabOption.SERVICES) && 'add-apps__button--active']"
          >
            {{ $t("WorkspaceEdit.addApps.buttons.services") }}
          </v-btn>
        </div>

        <popup-alerts v-if="ViewTabOption.SERVICES === viewType" />

        <apps-gallery
          v-if="ViewTabOption.APPS === viewType"
          :filter="filter"
          :app-category-item-map="appsInCategories"
          :apps-inserted="appsInserted"
          @increment="increment"
          @decrement="decrement"
        />

        <service-gallery
          v-else-if="ViewTabOption.SERVICES === viewType"
          :filter="filter"
          :app-category-item-map="serviceInCategories"
          :apps-inserted="appsInserted"
          @increment="increment"
          @decrement="decrement"
        />
      </div>
    </div>

    <div class="add-apps__footer">
      <v-btn class="add-apps__button" @click="$emit('cancel')" large>
        {{ $t("WorkspaceEdit.addApps.buttons.cancel") }}
      </v-btn>

      <v-btn
        @click="addSelection"
        :disabled="!hasPermissionToAddApps || (added.assets.length === 0 && added.services.length === 0)"
        color="primary"
        class="add-apps__button"
        rbt-target="addSelected"
        large
      >
        <span>{{ $t("WorkspaceEdit.addApps.buttons.add") }}</span>
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, nextTick, computed, PropType } from "@vue/composition-api"
import { CategoryItem, InsertedAppRegistry, ViewTabOption } from "../../../lib/types"
import { AppItem, ServiceItem, WorkspaceTemplate } from "@/types/workspace"
import { WorkspaceModule, UsersModule } from "@/store"
import { Dictionary } from "@/types/core"
import { AddedAssetsAndServices } from "./utils/types"
import { QUERY_VIEW_TYPE } from "@/config/constants"
import MiniAppCard from "./MiniAppCard.vue"
import Categories from "./Categories.vue"
import PopupAlerts from "./PopupAlerts.vue"
import AppsGallery from "./AppsGallery.vue"
import ServiceGallery from "./ServiceGallery.vue"
import SearchApp from "./Search.vue"
import router, { replace } from "@/router"
import "../../../scss/_addApps.scss"

export default defineComponent({
  name: "AppsContainer",
  props: {
    workspace: {
      type: Object as PropType<WorkspaceTemplate>,
      required: true
    }
  },
  setup(_, ctx) {
    const workspaceModule = WorkspaceModule()
    const usersModule = UsersModule()
    const categoryTagsSelected = ref<string[]>([])
    const snippetSearch = ref("")
    const appsInserted = ref<Dictionary<InsertedAppRegistry>>({})
    const viewType = ref<ViewTabOption>(ViewTabOption.APPS)

    const added = computed(() => {
      return Object.values(appsInserted.value).reduce(
        (addedElements: AddedAssetsAndServices, inserted) => {
          for (let i = 0; i < inserted.count; i++) {
            if ("key" in inserted.app) {
              addedElements.assets.push({
                assetModelType: inserted.app.key
              })
            } else {
              addedElements.services.push({
                serviceType: inserted.app.type
              })
            }
          }

          return addedElements
        },
        {
          assets: [],
          services: []
        }
      )
    })

    const hasPermissionToAddApps = computed(() => {
      return usersModule.hasEditorPermissions
    })

    const filter = computed(() => {
      return {
        tags: categoryTagsSelected.value,
        search: snippetSearch.value
      }
    })

    const appsInCategories = computed(() => {
      const apps = workspaceModule.appItems

      return apps.reduce((categories, appItem) => {
        if (categories[appItem.category]) {
          const items = categories[appItem.category].items as AppItem[]
          items.push(appItem as AppItem)
        } else {
          categories[appItem.category] = {
            key: appItem.category,
            type: ViewTabOption.APPS,
            items: [appItem]
          }
        }

        return categories
      }, {} as Dictionary<CategoryItem<AppItem>>)
    })

    const serviceInCategories = computed(() => {
      const apps = workspaceModule.serviceItems

      return apps.reduce((categories, serviceItem) => {
        if (categories[serviceItem.category]) {
          const items = categories[serviceItem.category].items as ServiceItem[]
          items.push(serviceItem)
        } else {
          categories[serviceItem.category] = {
            key: serviceItem.category,
            type: ViewTabOption.SERVICES,
            items: [serviceItem]
          }
        }

        return categories
      }, {} as Dictionary<CategoryItem<ServiceItem>>)
    })

    const _routeQuery = computed(() => {
      if (QUERY_VIEW_TYPE in router.currentRoute.query) {
        return router.currentRoute.query[QUERY_VIEW_TYPE] as ViewTabOption
      }
      return null
    })

    const clearFilters = (clearableType: "category" | "snippet" | "all") => {
      switch (clearableType) {
        case "category":
          categoryTagsSelected.value = []
          break
        case "snippet":
          snippetSearch.value = ""
          break
        case "all": {
          categoryTagsSelected.value = []
          snippetSearch.value = ""
          break
        }
      }
    }

    const isViewType = (view: ViewTabOption) => {
      return viewType.value === view
    }

    const addSelection = () => {
      ctx.emit("add:apps", added.value)

      nextTick(() => {
        _resetRegistry()
      })
    }

    const increment = (app: AppItem | ServiceItem, type: ViewTabOption) => {
      const key = "key" in app ? app.key : app.type

      if (appsInserted.value[key]) {
        const registry = appsInserted.value[key]
        const maxCount = app.maxCount - app.currentCount

        if (registry.count < maxCount) {
          appsInserted.value = {
            ...appsInserted.value,
            [key]: {
              app,
              type,
              count: registry.count + 1
            }
          }
        }
      } else {
        appsInserted.value = {
          ...appsInserted.value,
          [key]: {
            app,
            type,
            count: app.maxCount - app.currentCount > 0 ? 1 : 0
          }
        }
      }
    }

    const decrement = (app: AppItem | ServiceItem, type: ViewTabOption) => {
      const key = "key" in app ? app.key : app.type

      if (appsInserted.value[key]) {
        const registry = appsInserted.value[key]

        if (registry.count > 0) {
          appsInserted.value = {
            ...appsInserted.value,
            [key]: {
              app,
              type,
              count: registry.count - 1
            }
          }
        }
      }
    }

    const selectTab = (view: ViewTabOption) => {
      if (viewType.value !== view) {
        viewType.value = view
        clearFilters("all")

        replace({
          query: {
            [`${QUERY_VIEW_TYPE}`]: viewType.value
          }
        })
      }
    }

    const _resetRegistry = () => {
      Object.keys(appsInserted).forEach(key => delete appsInserted.value[key])
    }

    const _adjustViewType = () => {
      if (_routeQuery.value) {
        viewType.value = _routeQuery.value
      }
    }

    _adjustViewType()

    return {
      filter,
      viewType,
      isViewType,
      serviceInCategories,
      selectTab,
      added,
      categoryTagsSelected,
      hasPermissionToAddApps,
      clearFilters,
      snippetSearch,
      appsInCategories,
      appsInserted,
      increment,
      decrement,
      ViewTabOption,
      addSelection
    }
  },
  components: {
    PopupAlerts,
    ServiceGallery,
    AppsGallery,
    SearchApp,
    Categories,
    MiniAppCard
  }
})
</script>
